// deletion_audit_log — who/when/what for every cascading location delete.
//
// WHY (the capture-data-loss incident). An admin building-delete ran an
// unguarded cascade that hard-deleted a building's capture_sessions +
// capture_items, wiping multiple live surveys — and left NO record. The
// root-cause investigation could confirm the deletion's fingerprint but not who
// ran it or when, because delete-building.ts recorded nothing ("closing that gap
// is itself a fix item — and would have made this diagnosis instant",
// the root-cause analysis in PR #728, §6). This table is that
// record: one row per cascading delete of a building / base / site, with the
// acting admin, the target, the override flag, and the exact rows removed.
//
// Design (mirrors the other forensic/inbox tables — deleted_capture_items,
// capture_recovery_dump):
//   - NO foreign key to the target (building/base/site) — it is GONE by the
//     time anyone reads this row, so an FK would be impossible to satisfy. The
//     only FK is actor_user_id -> users.id (restrict): the acting admin is a
//     live, authed user we preserve for the trail.
//   - Counts are captured from the cascade's DELETE ... RETURNING, so they are
//     exact (not a pre-count that could race). archived_capture_items_count is
//     how many were preserved to deleted_capture_items before the cascade.
//   - NOT synced to devices (server-only forensic record).

import {
  boolean,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core'

import { users } from './schema'

export const deletionAuditLog = pgTable(
  'deletion_audit_log',
  {
    id: uuid('id').primaryKey().defaultRandom(),

    // What was deleted. `target_type` is 'building' | 'base' | 'site' (text, not
    // an enum, so adding a future target level needs no migration). No FK — the
    // target row no longer exists.
    targetType: text('target_type').notNull(),
    targetId: uuid('target_id').notNull(),
    targetName: text('target_name').notNull(),
    contractId: uuid('contract_id').notNull(),

    // Who ran it (the authenticated admin) — the one live FK.
    actorUserId: uuid('actor_user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    actorDisplayName: text('actor_display_name').notNull(),

    // Whether the in-progress-session safety guard was overridden (force=true).
    forced: boolean('forced').notNull(),
    // In-progress capture sessions on the target at delete time (the at-risk
    // signal — non-zero here with forced=true means live surveys were destroyed).
    activeSessionCount: integer('active_session_count').notNull(),

    // Exact rows the cascade removed (from DELETE ... RETURNING).
    captureItemsCount: integer('capture_items_count').notNull(),
    captureSessionsCount: integer('capture_sessions_count').notNull(),
    spacesCount: integer('spaces_count').notNull(),
    floorsCount: integer('floors_count').notNull(),

    // Rows preserved to deleted_capture_items before the cascade (0 until the
    // archive fast-follow ships).
    archivedCaptureItemsCount: integer('archived_capture_items_count')
      .notNull()
      .default(0),

    createdAt: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    targetIdx: index('deletion_audit_log_target_idx').on(
      t.targetType,
      t.targetId,
    ),
    actorIdx: index('deletion_audit_log_actor_idx').on(t.actorUserId),
    createdAtIdx: index('deletion_audit_log_created_at_idx').on(t.createdAt),
  }),
)

export type DeletionAuditLogInsert = typeof deletionAuditLog.$inferInsert
